/**
 * AWS Monitor Component
 * Monitor AWS services, costs, and infrastructure
 * Executive-grade dashboard using shared monitoring design system
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkIcon,
  AttachMoney as CostIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import { MonitoringChart } from '../charts/ChartComponents';
import CloudWatchLogsViewer from './CloudWatchLogsViewer';
import {
  getMonitoringColors,
  mapStatus,
  kpiCardSx,
  chartCardSx,
  monoValueSx,
  getChartTheme,
} from './monitoringDesignSystem';
import {
  MonitoringPageContainer,
  MonitoringHeroBanner,
  MonitoringKPICard,
  MonitoringChartCard,
  MonitoringDonutGauge,
  ServiceStatusRow,
  AutoRefreshIndicator,
} from './MonitoringComponents';

// ---------------------------------------------------------------------------
// Interfaces (preserved)
// ---------------------------------------------------------------------------

interface AWSService {
  name: string;
  status: 'operational' | 'degraded' | 'outage';
  region: string;
  uptime: number;
  lastUpdate: string;
}

interface AWSCosts {
  daily: number;
  monthly: number;
  projected: number;
  services: Array<{
    name: string;
    cost: number;
    percentage: number;
  }>;
}

interface AWSResources {
  ec2: {
    instances: number;
    running: number;
    stopped: number;
  };
  rds: {
    instances: number;
    available: number;
    maintenance: number;
  };
  s3: {
    buckets: number;
    totalSize: number;
  };
  lambda: {
    functions: number;
    invocations: number;
    errors: number;
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AWSMonitor: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getMonitoringColors(mode);
  const dispatch = useDispatch<AppDispatch>();

  // --- State (preserved) ---
  const [services, setServices] = useState<AWSService[]>([]);
  const [costs, setCosts] = useState<AWSCosts | null>(null);
  const [resources, setResources] = useState<AWSResources | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [alarms, setAlarms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // The hour-scale pill row that used to sit here was DEAD: `selectedTimeRange`
  // was a useCallback dependency and nothing else — no endpoint took it, no
  // panel narrowed to it. Removed rather than restyled.

  // --- Data loading (preserved) ---
  const loadAWSData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [awsCostData, alarmsResponse] = await Promise.all([
        apiService.getAWSCostAnalytics().catch(() => null),
        apiService.getAWSAlarms().catch(() => null),
      ]);

      // Use real AWS Cost Explorer data (same source as AIAWSMonitor)
      if (awsCostData) {
        setCosts({
          daily: awsCostData.dailyAWSCosts?.rawValue ?? 0,
          monthly: awsCostData.mtdAWSCosts?.rawValue ?? 0,
          projected: awsCostData.ytdAWSCosts?.rawValue ?? 0,
          services: [],
        });
      } else {
        setCosts(null);
      }

      // Extract alarms — backend returns {success, alarms: [...]} wrapped by fetchWithAuth
      const rawAlarms = alarmsResponse?.data?.alarms || alarmsResponse?.data || [];
      const normalizedAlarms = (Array.isArray(rawAlarms) ? rawAlarms : []).map((a: any) => ({
        ...a,
        state: a.state || a.StateValue || 'INSUFFICIENT_DATA',
      }));
      setAlarms(normalizedAlarms);
    } catch (error) {
      console.error('Failed to fetch AWS data:', error);
      dispatch(addNotification({ type: 'warning', message: 'Some AWS monitoring data unavailable' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadAWSData();
  }, [loadAWSData]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadAWSData, 60000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, loadAWSData]);

  // --- Utility functions (preserved) ---
  const formatCost = (cost: number | undefined | null) => `$${(cost ?? 0).toFixed(2)}`;
  const formatStorage = (sizeGB: number) => {
    if (sizeGB < 1024) return `${sizeGB.toFixed(1)} GB`;
    return `${(sizeGB / 1024).toFixed(1)} TB`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational': return 'success';
      case 'degraded': return 'warning';
      case 'outage': return 'error';
      default: return 'default';
    }
  };

  // --- Computed values ---
  const operationalCount = services.filter((s) => s.status === 'operational').length;
  const degradedCount = services.filter((s) => s.status === 'degraded').length;
  const outageCount = services.filter((s) => s.status === 'outage').length;
  const awsHealth = mapStatus(
    outageCount > 0 ? 'outage' : degradedCount > 0 ? 'degraded' : 'operational'
  );
  const awsBannerLabel =
    awsHealth === 'healthy' ? 'ALL AWS SERVICES OPERATIONAL' :
    awsHealth === 'warning' ? 'AWS SERVICES DEGRADED' : 'AWS SERVICES OUTAGE';
  const awsBannerSummary = `${operationalCount}/${services.length} services operational · Region: us-east-2`;
  const activeAlarmCount = alarms.filter((a) => a.state === 'ALARM').length;

  // --- Error state ---
  if (error) {
    return (
      <MonitoringPageContainer>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button size="small" onClick={() => { setError(null); loadAWSData(); }} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Alert>
      </MonitoringPageContainer>
    );
  }

  // --- Loading state ---
  if (loading && services.length === 0) {
    return (
      <MonitoringPageContainer>
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 3 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </MonitoringPageContainer>
    );
  }

  return (
    <MonitoringPageContainer>
      {/* Hero Banner */}
      <MonitoringHeroBanner
        status={awsHealth}
        title={awsBannerLabel}
        subtitle={awsBannerSummary}
        actions={
          <>
            <AutoRefreshIndicator
              autoRefresh={autoRefresh}
              onToggle={() => setAutoRefresh(!autoRefresh)}
              onRefresh={loadAWSData}
            />
          </>
        }
      />

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Services Online"
            value={`${operationalCount}/${services.length}`}
            accentColor={c.healthy}
            icon={<CloudIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Active Alarms"
            value={activeAlarmCount}
            accentColor={activeAlarmCount > 0 ? c.critical : c.textMuted}
            icon={<WarningIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="Daily Cost"
            value={costs ? formatCost(costs.daily) : '$--'}
            accentColor={c.textPrimary}
            icon={<CostIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MonitoringKPICard
            label="ECS Tasks"
            value={resources?.ec2?.running ?? '--'}
            accentColor={c.textPrimary}
            icon={<StorageIcon sx={{ fontSize: 32 }} />}
          />
        </Grid>
      </Grid>

      {/* Alarm Donut + Service Health */}
      {(alarms.length > 0 || services.length > 0) && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <MonitoringChartCard title="Alarm Status">
              {alarms.length > 0 ? (
                <MonitoringDonutGauge
                  data={[
                    { label: 'OK', value: alarms.filter((a) => a.state === 'OK').length, color: c.healthy },
                    { label: 'Alarm', value: alarms.filter((a) => a.state === 'ALARM').length, color: c.critical },
                    { label: 'No Data', value: alarms.filter((a) => a.state === 'INSUFFICIENT_DATA').length, color: c.unknown },
                  ]}
                  centerValue={alarms.length}
                  centerLabel="alarms"
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: c.healthy, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: c.textSecondary }}>No alarms configured</Typography>
                </Box>
              )}
            </MonitoringChartCard>
          </Grid>
          <Grid item xs={12} md={8}>
            <MonitoringChartCard title="Service Health" badge="live">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {services.map((service, i) => (
                  <ServiceStatusRow
                    key={i}
                    name={service.name}
                    status={mapStatus(service.status)}
                    detail={`${(service.uptime ?? 0).toFixed(1)}%`}
                    rightContent={
                      <Chip
                        label={service.status.toUpperCase()}
                        size="small"
                        color={getStatusColor(service.status) as any}
                        variant="outlined"
                      />
                    }
                  />
                ))}
                {services.length === 0 && (
                  <Typography variant="body2" sx={{ color: c.textSecondary, textAlign: 'center', py: 2 }}>
                    No services found
                  </Typography>
                )}
              </Box>
            </MonitoringChartCard>
          </Grid>
        </Grid>
      )}

      {/* Cost Overview */}
      {costs && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MonitoringKPICard
                label="Daily AWS Cost"
                value={formatCost(costs.daily)}
                accentColor="#4A90E2"
                icon={<CostIcon sx={{ fontSize: 32 }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MonitoringKPICard
                label="MTD AWS Cost"
                value={formatCost(costs.monthly)}
                accentColor="#7B68EE"
                icon={<CostIcon sx={{ fontSize: 32 }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MonitoringKPICard
                label="YTD AWS Cost"
                value={formatCost(costs.projected)}
                accentColor="#50C878"
                icon={<TrendingUpIcon sx={{ fontSize: 32 }} />}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <MonitoringChartCard title="Service Cost Breakdown">
                {(costs.services ?? []).map((service, index) => (
                  <Box key={index} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: c.textPrimary }}>{service.name}</Typography>
                      <Typography variant="body2" sx={{ ...monoValueSx, color: c.textPrimary }}>
                        {formatCost(service.cost)} ({service.percentage.toFixed(1)}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={service.percentage}
                      sx={{
                        height: 6,
                        borderRadius: 1,
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                      }}
                    />
                  </Box>
                ))}
              </MonitoringChartCard>
            </Grid>
            <Grid item xs={12} md={4}>
              {costs.projected > costs.monthly * 1.2 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    YTD costs are 20% higher than current monthly spend.
                    Review usage patterns and consider optimization.
                  </Typography>
                </Alert>
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {/* AWS Resources */}
      {resources && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MonitoringKPICard
              label="EC2 Instances"
              value={resources.ec2.instances}
              accentColor={c.healthy}
              subtitle={`${resources.ec2.running} running · ${resources.ec2.stopped} stopped`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MonitoringKPICard
              label="RDS Instances"
              value={resources.rds.instances}
              accentColor="#7B68EE"
              subtitle={`${resources.rds.available} available${resources.rds.maintenance > 0 ? ` · ${resources.rds.maintenance} maintenance` : ''}`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MonitoringKPICard
              label="S3 Storage"
              value={resources.s3.buckets}
              accentColor="#4A90E2"
              subtitle={`${formatStorage(resources.s3.totalSize)} total`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MonitoringKPICard
              label="Lambda Functions"
              value={resources.lambda.functions}
              accentColor={c.warning}
              subtitle={`${resources.lambda.invocations.toLocaleString()} invocations${resources.lambda.errors > 0 ? ` · ${resources.lambda.errors} errors` : ''}`}
            />
          </Grid>
        </Grid>
      )}

      {/* Metrics Chart */}
      {chartData && (
        <Box sx={{ mb: 3 }}>
          <MonitoringChartCard title="AWS Performance Metrics" badge="realtime">
            <MonitoringChart
              data={chartData}
              title="AWS Infrastructure Performance"
              height={350}
              type="area"
            />
          </MonitoringChartCard>
        </Box>
      )}

      {/* Service Details Accordions */}
      <MonitoringChartCard title="Service Details">
        {services.length === 0 && (
          <Typography variant="body2" sx={{ color: c.textSecondary, textAlign: 'center', py: 3 }}>
            No service data available
          </Typography>
        )}
        {services.map((service, index) => (
          <Accordion
            key={index}
            sx={{
              bgcolor: c.cardBg,
              border: `1px solid ${c.cardBorder}`,
              '&:before': { display: 'none' },
              mb: 0.5,
              borderRadius: '8px !important',
              overflow: 'hidden',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: c.textSecondary }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                {service.status === 'operational' ? (
                  <CheckCircleIcon sx={{ color: c.healthy, fontSize: 20 }} />
                ) : service.status === 'degraded' ? (
                  <WarningIcon sx={{ color: c.warning, fontSize: 20 }} />
                ) : (
                  <ErrorIcon sx={{ color: c.critical, fontSize: 20 }} />
                )}
                <Typography sx={{ fontWeight: 600, color: c.textPrimary }}>{service.name}</Typography>
                <Typography variant="body2" sx={{ ml: 'auto', mr: 2, color: c.textSecondary }}>
                  {service.region} | {(service.uptime ?? 0).toFixed(1)}% uptime
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: c.textSecondary, mb: 1 }}>
                    Service Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: c.textPrimary }}>
                    <strong>Status:</strong> {service.status}
                  </Typography>
                  <Typography variant="body2" sx={{ color: c.textPrimary }}>
                    <strong>Region:</strong> {service.region}
                  </Typography>
                  <Typography variant="body2" sx={{ color: c.textPrimary }}>
                    <strong>Uptime:</strong> {(service.uptime ?? 0).toFixed(2)}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: c.textPrimary }}>
                    <strong>Last Update:</strong> {new Date(service.lastUpdate).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: c.textSecondary, mb: 1 }}>
                    Health Status
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={service.uptime}
                    sx={{
                      mb: 1,
                      height: 8,
                      borderRadius: 1,
                      bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    }}
                    color={getStatusColor(service.status) as any}
                  />
                  <Typography variant="caption" sx={{ color: c.textMuted }}>
                    Service health: {service.uptime > 99 ? 'Excellent' : service.uptime > 95 ? 'Good' : 'Needs Attention'}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </MonitoringChartCard>

      {/* CloudWatch Logs */}
      <Box sx={{ mt: 3 }}>
        <CloudWatchLogsViewer />
      </Box>
    </MonitoringPageContainer>
  );
};

export default AWSMonitor;
